import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Star as StarIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { PageHeader, LoadingSpinner } from '../../components/common';
import { usePromptViewModel } from '../../viewmodels';

const PromptDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const promptId = parseInt(id!, 10);

  const { prompts, updatePrompt, isUpdating, setDefault, isSettingDefault } = usePromptViewModel();
  const prompt = prompts.find((p) => p.id === promptId);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template: '',
    is_active: true,
    is_shared: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [extractedVariables, setExtractedVariables] = useState<string[]>([]);

  useEffect(() => {
    if (prompt) {
      setFormData({
        name: prompt.name,
        description: prompt.description,
        template: prompt.template,
        is_active: prompt.is_active,
        is_shared: prompt.is_shared,
      });
      setExtractedVariables(prompt.variables);
    }
  }, [prompt]);

  // 템플릿에서 변수 추출
  useEffect(() => {
    const variableMatches = formData.template.match(/\{(\w+)\}/g) || [];
    const variables = [...new Set(variableMatches.map((v) => v.replace(/[{}]/g, '')))];
    setExtractedVariables(variables);
  }, [formData.template]);

  const handleSave = async () => {
    try {
      setError(null);
      await updatePrompt({
        id: promptId,
        data: {
          ...formData,
          variables: extractedVariables,
        },
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('저장에 실패했습니다.');
    }
  };

  const handleSetDefault = async () => {
    try {
      await setDefault(promptId);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('기본 프롬프트 설정에 실패했습니다.');
    }
  };

  if (!prompt) {
    return <LoadingSpinner />;
  }

  return (
    <Box>
      <PageHeader
        title="프롬프트 편집"
        breadcrumbs={[
          { label: '프롬프트', path: '/prompts' },
          { label: prompt.name },
        ]}
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!prompt.is_default && (
              <Button
                variant="outlined"
                startIcon={<StarIcon />}
                onClick={handleSetDefault}
                disabled={isSettingDefault}
              >
                기본으로 설정
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/prompts')}
            >
              목록으로
            </Button>
          </Box>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          저장되었습니다.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                기본 정보
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="프롬프트명"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="설명"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      />
                    }
                    label="활성화"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_shared}
                        onChange={(e) => setFormData({ ...formData, is_shared: e.target.checked })}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {formData.is_shared ? <PublicIcon fontSize="small" /> : <LockIcon fontSize="small" />}
                        {formData.is_shared ? '공유 (모든 사용자)' : '비공개 (본인만)'}
                      </Box>
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                프롬프트 템플릿
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                변수는 <code>{'{변수명}'}</code> 형식으로 작성합니다.
                AI 필드 자동채우기에서 <code>{'{content}'}</code>에 STT 텍스트가,
                <code>{'{fields}'}</code>에 템플릿 필드 목록이 자동으로 주입됩니다.
              </Alert>
              <TextField
                fullWidth
                value={formData.template}
                onChange={(e) => setFormData({ ...formData, template: e.target.value })}
                multiline
                rows={20}
                placeholder={`다음은 상담 녹음을 텍스트로 변환한 내용입니다. 이 내용을 분석하여 아래 양식 필드에 맞는 정보를 추출해주세요.

## 상담 내용:
{content}

## 추출할 필드:
{fields}

## 지시사항:
1. 상담 내용에서 각 필드에 해당하는 정보를 찾아 추출하세요.
2. 날짜 필드는 YYYY-MM-DD 형식으로, 시간은 HH:MM 형식으로 작성하세요.
3. 내용이 명확하지 않거나 찾을 수 없는 필드는 빈 문자열("")로 남겨두세요.
4. 상담내용이나 기타사항 같은 긴 텍스트 필드는 핵심 내용을 요약하여 작성하세요.
5. 반드시 JSON 형식으로만 응답하세요.

## 응답 형식 (JSON):
{
  "field_id1": "추출된 값",
  "field_id2": "추출된 값"
}`}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isUpdating}
            >
              {isUpdating ? '저장 중...' : '저장'}
            </Button>
            <Button variant="outlined" onClick={() => navigate('/prompts')}>
              취소
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                상태
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={prompt.is_active ? '활성' : '비활성'}
                  color={prompt.is_active ? 'success' : 'default'}
                />
                {prompt.is_default && (
                  <Chip label="기본 프롬프트" color="warning" icon={<StarIcon />} />
                )}
                <Chip
                  icon={prompt.is_shared ? <PublicIcon /> : <LockIcon />}
                  label={prompt.is_shared ? '공유' : '비공개'}
                  color={prompt.is_shared ? 'info' : 'default'}
                  variant="outlined"
                />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                추출된 변수
              </Typography>
              {extractedVariables.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  템플릿에 변수가 없습니다.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {extractedVariables.map((v) => (
                    <Chip
                      key={v}
                      label={`{${v}}`}
                      size="small"
                      variant="outlined"
                      color={v === 'content' || v === 'fields' ? 'primary' : 'default'}
                    />
                  ))}
                </Box>
              )}
              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" color="text.secondary">
                <strong>시스템 변수:</strong>
                <br />
                - <code>{'{content}'}</code>: STT 텍스트
                <br />
                - <code>{'{fields}'}</code>: 템플릿 필드 목록
              </Typography>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                정보
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    등록자
                  </Typography>
                  <Typography variant="body2">{prompt.created_by_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    등록일
                  </Typography>
                  <Typography variant="body2">
                    {new Date(prompt.created_at).toLocaleString('ko-KR')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    수정일
                  </Typography>
                  <Typography variant="body2">
                    {new Date(prompt.updated_at).toLocaleString('ko-KR')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PromptDetailPage;
